import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Api from "../../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../../core/services/toast";
import {Box, Checkbox, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Tab, Tabs, Typography,} from "@mui/material";
import PartnerDataGrid, {DataGridColumnAlignments, DataGridColumnTypes, DataGridDensities} from "../../../../../../../containers/data-grid";
import {useTranslation} from "react-i18next";
import usePreventativeCorrection, {isPreventativeFieldActiveForIssue, isPreventativeFieldEditable} from "./use-preventative-correction";
import AddNewProductDialog from "../../../../../../../components/dialogs/add-new-product";
import CreateNewTagsDialog from "../../../../../../../components/dialogs/create-new-tags";
import CreateCustomerDialog from "../../../../../../../components/dialogs/create-customer";
import AddVendorModal from "../../../../../../../components/dialogs/add-vendor";
import ImportExternalProductDialog from "../../../../../../../components/dialogs/import-external-product";
import ImportExternalCustomerDialog from "../../../../../../../components/dialogs/import-external-customer";
import Dialog from "../../../../../../../components/base/dialog";
import Button from "../../../../../../../components/base/button/button";
import {Switch} from "../../../../../../../components/base/switch";
import PreventativeFieldCell from "./cells/preventative-field-cell";
import PreventativeColumnHeader from "./preventative-column-header";
import {PreventativeSkipCheckbox} from "./preventative-skip-field-cell";
import {
    getPreventativeAutocompleteActionDialogs,
    getPreventativeEditableColumnWidth,
    isPreventativeAutocompleteType,
    isPreventativeFieldSkippable,
    PreventativeAutocompleteType,
    resolveFieldSkipTarget,
} from "../../engine/preventative-interactions/shared";
import {BaseImportTransformer} from "../../engine";
import {PreventativeGridActionsProvider} from "./preventative-grid-actions-context";
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";
import Utils from "../../../../../../../../core/services/utils/utils";

const IGNORE_ROW_FIELD_KEY = '_ignoreRow';

const ADD_PROPERTY_OPTION_ERROR_KEY_BY_KIND = {
    required: 'addPropertyOptionRequired',
    number: 'addPropertyOptionInvalidNumber',
    decimal: 'addPropertyOptionInvalidDecimal',
    money: 'addPropertyOptionInvalidMoney',
    fraction: 'addPropertyOptionInvalidFraction',
    text: 'addPropertyOptionRequired',
};

const mergePropertyOptionsByPropertyId = (base, overlay) => {
    const merged = {...(base ?? {})};
    for (const [propertyId, options] of Object.entries(overlay ?? {})) {
        const existing = merged[propertyId] ?? [];
        const existingIds = new Set(existing.map((option) => String(option.id)));
        const existingTitles = new Set(
            existing.map((option) => (option.title ?? '').trim().toUpperCase()),
        );
        const additions = (options ?? []).filter(
            (option) =>
                !existingIds.has(String(option.id)) &&
                !existingTitles.has((option.title ?? '').trim().toUpperCase()),
        );
        if (additions.length) {
            merged[propertyId] = [...existing, ...additions];
        }
    }
    return merged;
};

const fieldHasOwnPatch = (patch, fieldKey) =>
    patch && Object.prototype.hasOwnProperty.call(patch, fieldKey);

const isEditableElementColumn = (field) =>
    isPreventativeAutocompleteType(field.type) || field.type === 'input' || field.type === 'toggle';

/**
 * @param {Object} field
 * @param {Array<Object>} issues
 * @param {Set<number>} ignoredRowIndices
 */
const columnRequiresAttention = (field, issues, ignoredRowIndices) => {
    if (field.key === 'partNumber' || field.requiresAttention === true) {
        return issues.some(
            (issue) =>
                !ignoredRowIndices.has(issue.rowIndex) &&
                isPreventativeFieldActiveForIssue(field, issue),
        );
    }
    if (!isPreventativeFieldEditable(field)) {
        return false;
    }
    return issues.some(
        (issue) =>
            !ignoredRowIndices.has(issue.rowIndex) &&
            isPreventativeFieldActiveForIssue(field, issue),
    );
};

const PreventativeCorrectionDialog = ({
                                          open,
                                          onClose,
                                          parsedRows,
                                          preventativeIssues,
                                          selectedDataType,
                                          mappingFields = [],
                                          staticDataContext = {},
                                          userSkippedRowIndices = [],
                                          onUserSkippedRowIndicesChange,
                                          userSkippedFieldCells = [],
                                          onSubmit,
                                      }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.preventativeCorrectionDialog'});
    const [onlyShowColumnsRequiringAttention, setOnlyShowColumnsRequiringAttention] = useState(false);
    const [localPropertyOptionsByPropertyId, setLocalPropertyOptionsByPropertyId] = useState({});
    const [addingPropertyOptionCellKey, setAddingPropertyOptionCellKey] = useState(null);

    const mergedStaticDataContext = useMemo(() => ({
        ...staticDataContext,
        propertyOptionsByPropertyId: mergePropertyOptionsByPropertyId(
            staticDataContext.propertyOptionsByPropertyId,
            localPropertyOptionsByPropertyId,
        ),
    }), [staticDataContext, localPropertyOptionsByPropertyId]);

    const {
        sections,
        patches,
        ignoredRowIndices,
        onPatchRow,
        toggleRowIgnored,
        isRowIgnored,
        isFieldSkipped,
        toggleSkippedFieldCell,
        skippedFieldCells,
        getSkipEvaluation,
        getColumnSkipState,
        toggleColumnSkipForSection,
        propagateRowValueToOthers,
        handleSubmit,
        canSubmit,
    } = usePreventativeCorrection({
        open,
        parsedRows,
        preventativeIssues,
        selectedDataType,
        mappingFields,
        onSubmit,
        staticDataContext: mergedStaticDataContext,
        userSkippedRowIndices,
        onUserSkippedRowIndicesChange,
        userSkippedFieldCells,
        t,
    });

    const [activeTab, setActiveTab] = useState(0);
    const [creationDialog, setCreationDialog] = useState({
        open: false,
        type: null,
        rowIndex: null,
        fieldKey: null,
        initialKeyword: '',
    });
    const [importDialog, setImportDialog] = useState({open: false, type: null, rowIndex: null, fieldKey: null});

    const currentSection = sections[activeTab];
    const currentSectionRef = useRef(currentSection);
    currentSectionRef.current = currentSection;

    useEffect(() => {
        if (activeTab >= sections.length) {
            setActiveTab(0);
        }
    }, [activeTab, sections.length]);

    useEffect(() => {
        if (open) {
            setActiveTab(0);
            setOnlyShowColumnsRequiringAttention(false);
            setLocalPropertyOptionsByPropertyId({});
            setAddingPropertyOptionCellKey(null);
            setCreationDialog({open: false, type: null, rowIndex: null, fieldKey: null, initialKeyword: ''});
            setImportDialog({open: false, type: null, rowIndex: null, fieldKey: null});
        }
    }, [open]);

    const addPropertyOptionForCell = useCallback(async (rowIndex, fieldKey) => {
        const section = currentSectionRef.current;
        const field = section?.fields?.find((candidate) => candidate.key === fieldKey);
        const issue = section?.issues?.find((entry) => entry.rowIndex === rowIndex);
        if (!field || !issue || field.type !== PreventativeAutocompleteType.INDUSTRY_PROPERTY_OPTIONS) {
            return;
        }

        const issueProps =
            typeof field.autocompletePropsFromIssue === 'function'
                ? field.autocompletePropsFromIssue(issue, mergedStaticDataContext)
                : (field.autocompleteProps ?? {});
        const propertyId = issueProps.propertyId ?? fieldKey.replace(/^property_/, '');
        const industryId = staticDataContext?.industryId;
        const technicalProperties = staticDataContext?.technicalPropertiesOfIndustry?.[industryId] ?? [];
        const propertyDefinition = technicalProperties.find(
            (property) => String(property.id) === String(propertyId),
        );
        const typeId = propertyDefinition?.industryPropertyTypeId ?? propertyDefinition?.typeId;
        const invalidPredefinedMeta = issue?.meta?.predefinedOptions?.[fieldKey];
        const contentToCreate =
            invalidPredefinedMeta?.unresolvedTokens?.[0] ??
            (typeof field.extractValue === 'function'
                ? field.extractValue(issue.originalRow?.[fieldKey], issue)
                : (issue.originalRow?.[fieldKey] ?? '').toString().trim());

        const validation = Utils.normalizeIndustryPropertyPredefinedOptionContent(Number(typeId), contentToCreate);
        if (!validation.valid) {
            ToastService.warning({
                message:
                    validation.message ||
                    t(ADD_PROPERTY_OPTION_ERROR_KEY_BY_KIND[validation.errorKind] ?? 'addPropertyOptionRequired'),
            });
            return;
        }

        const cellKey = `${rowIndex}:${fieldKey}`;
        setAddingPropertyOptionCellKey(cellKey);
        try {
            const response = await Api.addIndustryPropertyPropertyValue({
                content: validation.apiContent,
                industryPropertyId: propertyId,
            });
            if (!response?.resultFlag || !response.data) {
                return;
            }

            const newOption = {
                id: response.data.id,
                title: response.data.content ?? validation.apiContent,
            };

            BaseImportTransformer.sharedImportTransformContext.registerCreatedIndustryPropertyOption(
                propertyId,
                newOption,
            );

            setLocalPropertyOptionsByPropertyId((prev) => {
                const key = String(propertyId);
                return {
                    ...prev,
                    [key]: [...(prev[key] ?? []), newOption],
                };
            });

            const patchValue = issueProps.multiple ? [newOption] : newOption;
            onPatchRow(rowIndex, fieldKey, patchValue);
        } finally {
            setAddingPropertyOptionCellKey(null);
        }
    }, [mergedStaticDataContext, onPatchRow, staticDataContext?.industryId, staticDataContext?.technicalPropertiesOfIndustry, t]);

    const openCreationForCell = useCallback((rowIndex, fieldKey) => {
        const section = currentSectionRef.current;
        const field = section?.fields?.find((f) => f.key === fieldKey);
        if (!field) {
            return;
        }
        const {creationDialog: dialogType} = getPreventativeAutocompleteActionDialogs(field.type);
        if (!dialogType) {
            return;
        }
        const issue = section?.issues?.find((i) => i.rowIndex === rowIndex);
        const raw = issue?.originalRow?.[fieldKey];
        const initialKeyword =
            typeof raw === 'string'
                ? raw
                : raw?.name ?? raw?.sellerInfo?.name ?? '';
        setCreationDialog({open: true, type: dialogType, rowIndex, fieldKey, initialKeyword});
    }, []);

    const openImportForCell = useCallback((rowIndex, fieldKey) => {
        const section = currentSectionRef.current;
        const field = section?.fields?.find((f) => f.key === fieldKey);
        if (!field) {
            return;
        }
        const {importDialog: dialogType} = getPreventativeAutocompleteActionDialogs(field.type);
        if (!dialogType) {
            return;
        }
        setImportDialog({open: true, type: dialogType, rowIndex, fieldKey});
    }, []);

    const visibleFields = useMemo(() => {
        if (!currentSection?.fields) {
            return [];
        }
        if (!onlyShowColumnsRequiringAttention) {
            return currentSection.fields;
        }
        return currentSection.fields.filter((field) =>
            columnRequiresAttention(field, currentSection.issues, ignoredRowIndices),
        );
    }, [currentSection, onlyShowColumnsRequiringAttention, ignoredRowIndices]);

    const bulkHandlersByFieldKey = useMemo(() => {
        if (!currentSection) {
            return new Map();
        }
        const m = new Map();
        for (const field of visibleFields) {
            if (!isEditableElementColumn(field)) {
                continue;
            }
            m.set(field.key, {
                setForAll: (value) => propagateRowValueToOthers({currentSection, field, value}, 'all'),
                setForRemaining: (value) => propagateRowValueToOthers({currentSection, field, value}, 'remaining'),
            });
        }
        return m;
    }, [currentSection, visibleFields, propagateRowValueToOthers]);

    const columns = useMemo(() => {
        if (!currentSection) {
            return [];
        }

        const baseColumns = [
            {
                title: t('ignoreRow'),
                name: IGNORE_ROW_FIELD_KEY,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                reorderable: false,
                width: 60,
            },
            {
                title: t("csvRowNumber"),
                name: 'csvRowNumber',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                resizable: false,
                reorderable: false,
                width: 60,
            },
        ];

        const fieldColumns = visibleFields.map((field) => {
            const editable = isPreventativeFieldEditable(field);
            const skippable = field.skippable === true;
            const width = (editable
                ? getPreventativeEditableColumnWidth(field)
                : (field.columnWidth ?? 150)) + (skippable ? 28 : 0);
            const columnSkipState = getColumnSkipState(field, currentSection);

            return {
                title: () => (
                    <PreventativeColumnHeader
                        label={field.label}
                        skippable={skippable}
                        skipFieldHint={t('skipFieldHint')}
                        columnSkipChecked={columnSkipState.checked}
                        columnSkipIndeterminate={columnSkipState.indeterminate}
                        columnSkipDisabled={columnSkipState.disabled}
                        columnSkipTooltip={t('skipColumnForAll')}
                        onColumnSkipChange={(checked) => {
                            const applied = toggleColumnSkipForSection(field, currentSection, checked);
                            if (!applied && checked) {
                                ToastService.warning({message: t('skipColumnNoneApplied')});
                            }
                        }}
                    />
                ),
                headerSkipState: `${columnSkipState.checked}-${columnSkipState.indeterminate}-${columnSkipState.disabled}`,
                name: field.key,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width,
            };
        });

        return [...baseColumns, ...fieldColumns];
    }, [currentSection, visibleFields, t, getColumnSkipState, toggleColumnSkipForSection, skippedFieldCells]);

    const rows = useMemo(() => {
        if (!currentSection) {
            return [];
        }

        return currentSection.issues.map((issue) => {
            const rowIndex = issue.rowIndex;
            const patch = patches.get(rowIndex) || {};
            const rowIgnored = isRowIgnored(rowIndex);
            const cells = {
                csvRowNumber: rowIndex + 1,
                [IGNORE_ROW_FIELD_KEY]: (
                    <Checkbox
                        size="small"
                        checked={rowIgnored}
                        onChange={() => toggleRowIgnored(rowIndex)}
                        inputProps={{'aria-label': t('ignoreRow')}}
                        icon={<CheckboxIcon/>}
                        checkedIcon={<ActiveCheckboxIcon/>}
                    />
                ),
            };

            for (const field of visibleFields) {
                if (rowIgnored) {
                    cells[field.key] = (
                        <Typography
                            key={`${rowIndex}-${field.key}-removed`}
                            variant="body2"
                            color="text.disabled"
                            fontStyle="italic"
                        >
                            {t('rowRemoved')}
                        </Typography>
                    );
                    continue;
                }

                if (!isPreventativeFieldActiveForIssue(field, issue)) {
                    cells[field.key] = (
                        <Typography key={`${rowIndex}-${field.key}-na`} variant="body2" color="text.disabled">
                            —
                        </Typography>
                    );
                    continue;
                }

                const bulk = bulkHandlersByFieldKey.get(field.key);
                const originalRaw =
                    (field.type === 'text' || field.type === 'input') && typeof field.extractValue === 'function'
                        ? field.extractValue(issue.originalRow[field.key], issue)
                        : issue.originalRow[field.key];
                const hasPatch = fieldHasOwnPatch(patch, field.key);
                const bulkSourceValue = hasPatch ? patch[field.key] : undefined;
                const committedValue = hasPatch ? patch[field.key] : originalRaw;
                const skipTarget = resolveFieldSkipTarget(field);
                const fieldSkipped = isFieldSkipped(rowIndex, skipTarget);
                const skippable = isPreventativeFieldSkippable(field, issue);
                const skipEvaluation = skippable ? getSkipEvaluation(field, issue) : {allowed: false};
                const skipDisabled = !skipEvaluation.allowed && !fieldSkipped;

                const skipCheckbox = skippable ? (
                    <PreventativeSkipCheckbox
                        rowIndex={rowIndex}
                        fieldKey={skipTarget}
                        fieldLabel={field.label}
                        skipped={fieldSkipped}
                        skipDisabled={skipDisabled}
                        skipDisabledReason={skipEvaluation.reason}
                        onToggleSkip={toggleSkippedFieldCell}
                        t={t}
                    />
                ) : undefined;

                cells[field.key] = (
                    <PreventativeFieldCell
                        key={`${rowIndex}-${field.key}`}
                        rowIndex={rowIndex}
                        field={field}
                        issue={issue}
                        patchFieldKey={field.key}
                        staticDataContext={mergedStaticDataContext}
                        columnTitle={field.label}
                        originalRaw={originalRaw}
                        committedValue={committedValue}
                        bulkSourceValue={bulkSourceValue}
                        onPatchRow={onPatchRow}
                        setForAll={bulk?.setForAll}
                        setForRemaining={bulk?.setForRemaining}
                        skipCheckbox={skipCheckbox}
                        skipped={fieldSkipped}
                        skippedLabel={t('fieldSkipped')}
                        t={t}
                    />
                );
            }

            return {
                key: `preventative-row-${rowIndex}`,
                data: issue.originalRow,
                cells,
            };
        });
    }, [
        currentSection,
        visibleFields,
        patches,
        onPatchRow,
        bulkHandlersByFieldKey,
        mergedStaticDataContext,
        t,
        isRowIgnored,
        toggleRowIgnored,
        isFieldSkipped,
        toggleSkippedFieldCell,
        getSkipEvaluation,
        skippedFieldCells,
    ]);

    /**
     * Handles successful creation or import of an entity by patching the corresponding cell and closing the dialogs.
     * @type {(function(*): void)|*}
     */
    const handleSuccess = useCallback((data) => {
        const {rowIndex, fieldKey} = creationDialog.open ? creationDialog : importDialog;
        if (creationDialog.open && creationDialog.type === 'tag' && staticDataContext?.industryId) {
            BaseImportTransformer.sharedImportTransformContext.registerCreatedTag(
                staticDataContext.industryId,
                data,
            );
        }
        onPatchRow(rowIndex, fieldKey, data);
        setCreationDialog({open: false, type: null, rowIndex: null, fieldKey: null, initialKeyword: ''});
        setImportDialog({open: false, type: null, rowIndex: null, fieldKey: null});
    }, [creationDialog, importDialog, onPatchRow, staticDataContext?.industryId]);

    if (!sections.length) {
        return null;
    }

    return (
        <Dialog
            open={open}
            setOpen={onClose}
            maxWidth="md"
            className={'bound-dialog'}
        >
            <DialogTitle>
                <Box sx={{padding: 2, paddingBlockEnd: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="h6" fontWeight="bold">
                            {t('title')}
                        </Typography>
                        <FormControlLabel
                            sx={{m: 0}}
                            control={
                                <Switch
                                    checked={onlyShowColumnsRequiringAttention}
                                    onChange={(checked) => setOnlyShowColumnsRequiringAttention(checked)}
                                />
                            }
                            slotProps={{
                                typography: {
                                    variant: 'body2',
                                    sx: {marginInlineStart: 1, fontWeight: 'bold',}
                                },
                            }}
                            label={t('onlyShowColumnsRequiringAttention')}
                        />
                    </Box>
                    <Box sx={{marginInline: -2}}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, val) => setActiveTab(val)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {sections.map((section) => (
                                <Tab key={section.sectionKey} label={`${section.sectionTitle} (${section.issues.length})`}/>
                            ))}
                        </Tabs>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1}}>
                    <DialogContentText variant={'body2'}>
                        {t('description')}
                    </DialogContentText>
                    {
                        currentSection && (
                            <PreventativeGridActionsProvider
                                openCreationForCell={openCreationForCell}
                                openImportForCell={openImportForCell}
                                addPropertyOptionForCell={addPropertyOptionForCell}
                                addingPropertyOptionCellKey={addingPropertyOptionCellKey}
                            >
                                <PartnerDataGrid
                                    rows={rows}
                                    columns={columns}
                                    hideFooter
                                    hideExporting
                                    useVirtualizedRows
                                    useVirtualizedColumns={columns.length > 10}
                                    visibleRows={5}
                                    state={{pagination: {currentPage: 1, pageSize: rows.length,}, density: DataGridDensities.compact}}
                                    classNames={{toolbar: 'pt-1',}}
                                />
                            </PreventativeGridActionsProvider>
                        )
                    }
                </Box>
            </DialogContent>
            <DialogActions sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    onClick={onClose}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    color={'primary'}
                    type="submit"
                    borderRadius={'8px 0 8px 0'}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {t('applyCorrections')}
                </Button>
            </DialogActions>

            <AddNewProductDialog
                open={creationDialog.open && creationDialog.type === 'product'}
                setOpen={(val) => !val && setCreationDialog(prev => ({...prev, open: false}))}
                onSuccess={handleSuccess}
            />
            <CreateCustomerDialog
                open={creationDialog.open && creationDialog.type === 'customer'}
                close={() => setCreationDialog(prev => ({...prev, open: false}))}
                onSuccess={handleSuccess}
            />
            <AddVendorModal
                open={creationDialog.open && creationDialog.type === 'vendor'}
                setOpen={(val) => !val && setCreationDialog(prev => ({...prev, open: false}))}
                addVendor={async (forApi, onResponse) => {
                    const response = await Api.createVendorOnlineProfile(forApi);
                    onResponse(response);
                    if (response?.resultFlag) {
                        handleSuccess(response?.data);
                    }
                }}
            />
            <CreateNewTagsDialog
                open={creationDialog.open && creationDialog.type === 'tag'}
                close={() => setCreationDialog(prev => ({...prev, open: false}))}
                onSuccess={handleSuccess}
                limitToOne
                initialValues={{
                    industryId: staticDataContext?.industryId,
                    keyword: creationDialog.initialKeyword ?? '',
                }}
            />

            <ImportExternalProductDialog
                open={importDialog.open && importDialog.type === 'product'}
                setOpen={(val) => !val && setImportDialog(prev => ({...prev, open: false}))}
                onSuccess={handleSuccess}
            />
            <ImportExternalCustomerDialog
                open={importDialog.open && importDialog.type === 'customer'}
                setOpen={(val) => !val && setImportDialog(prev => ({...prev, open: false}))}
                onSuccess={handleSuccess}
            />
        </Dialog>
    );
};

export default PreventativeCorrectionDialog;
